import { Grid, makeStyles, Theme } from '@material-ui/core';
import { Body, Icon, Tooltip } from 'copilot-design-system';
import { useFormikContext } from 'formik';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 } from 'uuid';

import {
  Company,
  CONTRACT_COMPONENT_DEFAULT_HEIGHT,
  CONTRACT_COMPONENT_DEFAULT_WIDTH,
  DEFAULT_DATE_FORMAT,
  SignatureComponentType,
  User,
} from 'src/constants';
import { PortalConfigContext } from 'src/context';
import {
  AutoFillFieldKey,
  PredefinedAutofillKeys,
} from 'src/entities/Contract';
import {
  CustomFieldValue,
  isAddressCustomFieldValues,
} from 'src/legacy/components/ClientDetailsPage/clientDetailsTypes';
import { MapFieldTypeToDefaultProps } from 'src/legacy/components/CustomFieldsMenu';
import { NoCompanyIcon, SignatureDateIcon } from 'src/legacy/components/Icons';
import { MultiSelectOption } from 'src/legacy/components/Select/types';
import { SignatureSidebarButton } from 'src/legacy/components/Signature/SignatureComponents';
import MemoBaseTypography from 'src/legacy/components/Text/BaseTypography';
import { TruncatedText } from 'src/legacy/components/UI';
import { InputType } from 'src/services/api';
import { useCustomFields } from 'src/services/api/customFieldsApi';
import { RootState } from 'src/store';
import {
  CustomFieldEntityType,
  CustomFieldOption,
  CustomFieldType,
} from 'src/store/clients/types';
import {
  AddComponentAction,
  SetSelectedComponent,
} from 'src/store/signaturePage/actions';
import { ComponentCoordinatesType } from 'src/store/signaturePage/types';
import { ensureUnreachable } from 'src/utils/common_utils';
import { getFieldTypeUsingComponentType } from 'src/utils/ContractUtils';
import { toCamelCase } from 'src/utils/StringUtils';
import { isUUID, UUID } from 'src/utils/UUIDUtils';

import { ContractTemplateForm } from '../ContractBuilderAndSignerForm';

interface FieldInfo {
  type: CustomFieldType;
  id: AutoFillFieldKey;
  value: CustomFieldValue | null;
  title: string;
  label: string;
}

const CUSTOM_CLIENT_INPUT_TOOLTIP_TEXT = `These values are dynamically updated based on the selected recipient.`;

export const getSelectedRecipientAutoFieldValue = ({
  key,
  recipient,
  recipientCompany,
  customFieldsMap,
}: {
  key: string | PredefinedAutofillKeys;
  recipient?: User;
  recipientCompany?: Company;
  customFieldsMap: Record<string, CustomFieldOption<MultiSelectOption>>;
}): string | null => {
  const clientInfoFields = recipient?.fields;

  if (!clientInfoFields || !key) return null;

  if (isContractPredefinedKey(key)) {
    switch (key) {
      case PredefinedAutofillKeys.FirstName:
        return clientInfoFields.givenName;
      case PredefinedAutofillKeys.FullName:
        return `${clientInfoFields.givenName} ${clientInfoFields.familyName}`;
      case PredefinedAutofillKeys.LastName:
        return clientInfoFields.familyName;
      case PredefinedAutofillKeys.Email:
        return clientInfoFields.email;
      case PredefinedAutofillKeys.ShareDate: {
        return moment().format(DEFAULT_DATE_FORMAT);
      }
      case PredefinedAutofillKeys.Company: {
        return recipientCompany?.fields?.name || null;
      }
      default:
        return ensureUnreachable(key);
    }
  }

  const customField = customFieldsMap[key];
  if (!customField || !clientInfoFields?.customFields) return null;

  // Handle the case when the custom field type is 'multiSelect'.
  // Here we will separate multiple options using ","
  if (customField.type === 'multiSelect' && customField?.options?.length) {
    const tagOptions = customField.options;
    const tags = clientInfoFields.customFields[customField.id];
    const tagLabels: string[] = [];
    // For clients without multi-select custom fields value assigned, tags will be null, so we need to return early.
    if (!tagOptions || !tags) return '';
    tags.forEach((tag: string) => {
      const tagOption = tagOptions.find((i) => i.id === tag);
      if (tagOption) tagLabels.push(tagOption.label);
    });
    return tagLabels.join(', ');
  }

  // Handle the case when the custom field type is 'address'.
  // Return the fullAddress string instead of the entire address object.
  if (customField.type === 'address') {
    const addressValue = clientInfoFields.customFields[customField.id];
    if (addressValue && isAddressCustomFieldValues(addressValue)) {
      return addressValue.fullAddress;
    }
    return null;
  }

  return clientInfoFields.customFields[customField.id] as string | null;
};

const useStyles = makeStyles((theme: Theme) => ({
  customInputSignatureButton: {
    maxWidth: CONTRACT_COMPONENT_DEFAULT_WIDTH,
    flex: 1,
    '& svg': {
      height: 16,
    },
    '& .MuiButton-label div': {
      maxWidth: 120,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  inputTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
}));

const isContractPredefinedKey = (key: string): key is PredefinedAutofillKeys =>
  Object.values(PredefinedAutofillKeys).includes(key as PredefinedAutofillKeys);

export const SelectedRecipientInfoInputs = () => {
  const dispatch = useDispatch();
  const { values: formikValues } = useFormikContext<ContractTemplateForm>();
  const clients = useSelector(
    (state: RootState) => state.clients.activeClients,
  );
  const companies = useSelector(
    (state: RootState) => state.clients.activeCompanies,
  );
  const portalConfig = React.useContext(PortalConfigContext);

  const classes = useStyles();

  const customFieldsMap = useCustomFields(CustomFieldEntityType.CLIENT);

  const selectedRecipientId =
    formikValues?.clientId ?? formikValues?.recipientId;
  const selectedRecipientCompanyId = formikValues?.companyId;

  /**
   * Handles the component drop action.
   * @param {ComponentCoordinatesType} coords - The coordinates where the component is dropped.
   * @param {SignatureComponentType} componentType - The type of the dropped component.
   * @param {string} value - Actual value containing recipient info
   */
  const handleComponentDropped = (
    coords: ComponentCoordinatesType,
    componentType: SignatureComponentType,
    value: string,
    autofillKey: AutoFillFieldKey,
  ) => {
    const key = v4();
    const stagedComponent = {
      componentType,
      inputType: InputType.AutoFill,
      fieldType: getFieldTypeUsingComponentType(componentType),
      key,
      fields: { key, value },
      ...coords,
      height: CONTRACT_COMPONENT_DEFAULT_HEIGHT,
      width: CONTRACT_COMPONENT_DEFAULT_WIDTH,
      autoFillField: autofillKey,
    };

    dispatch(AddComponentAction(stagedComponent));
    dispatch(SetSelectedComponent(stagedComponent.fields.key));
  };

  const clientCustomInputs = useMemo(() => {
    const getClientCustomInputValue = (key: string) =>
      getSelectedRecipientAutoFieldValue({
        key,
        recipient: clients.find((c) => c.id === selectedRecipientId),
        recipientCompany: companies.find(
          (c) => c.id === selectedRecipientCompanyId,
        ),
        customFieldsMap,
      });

    const defaultFields: FieldInfo[] = [
      {
        type: 'text',
        id: PredefinedAutofillKeys.FullName,
        value: getClientCustomInputValue(PredefinedAutofillKeys.FullName),
        title: 'Full name',
        label: `{{client.${PredefinedAutofillKeys.FullName}}}`,
      },
      {
        type: 'text',
        id: PredefinedAutofillKeys.FirstName,
        value: getClientCustomInputValue(PredefinedAutofillKeys.FirstName),
        title: 'First name',
        label: `{{client.${PredefinedAutofillKeys.FirstName}}}`,
      },
      {
        type: 'text',
        id: PredefinedAutofillKeys.LastName,
        value: getClientCustomInputValue(PredefinedAutofillKeys.LastName),
        title: 'Last name',
        label: `{{client.${PredefinedAutofillKeys.LastName}}}`,
      },
      {
        type: 'email',
        id: PredefinedAutofillKeys.Email,
        value: getClientCustomInputValue(PredefinedAutofillKeys.Email),
        title: 'Email',
        label: `{{client.${PredefinedAutofillKeys.Email}}}`,
      },
    ];

    if (portalConfig.features.disableCompanies !== true) {
      defaultFields.push({
        type: 'text',
        id: PredefinedAutofillKeys.Company,
        value: getClientCustomInputValue(PredefinedAutofillKeys.Company),
        title: 'Company',
        label: `{{client.${PredefinedAutofillKeys.Company}}}`,
      });
    }

    // Using client's custom properties list to list autofill inputs
    const portalCustomFields = Object.values(customFieldsMap).map((f) => {
      if (!isUUID(f.id)) {
        console.error(`Invalid UUID for custom field: ${f.id}`);
      }
      return {
        type: f.type,
        id: f.id as UUID, // casting since we're doing the check above
        value: getClientCustomInputValue(f.id),
        title: f.name,
        label: `{{client.${toCamelCase(f.name)}}}`,
      };
    });

    const allFields = [...defaultFields, ...portalCustomFields];
    if (!selectedRecipientId) {
      return allFields;
    }

    return allFields.filter((c) => c.value);
  }, [
    clients,
    companies,
    customFieldsMap,
    portalConfig.features.disableCompanies,
    selectedRecipientCompanyId,
    selectedRecipientId,
  ]);

  // This function is used to get the value of the field based on the type of the field.
  // For address custom fields, we need to return the full address.
  const getFieldValue = React.useCallback(
    (value: CustomFieldValue | null): string => {
      if (!value) return '';
      if (isAddressCustomFieldValues(value)) {
        return value.fullAddress;
      }
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      return value;
    },
    [],
  );

  return (
    <>
      <div className={classes.inputTitle}>
        <MemoBaseTypography fontType="12Medium">
          Autofill fields
        </MemoBaseTypography>
        <Tooltip
          position="bottom"
          triggerClassname="inline-block"
          content={
            <Body size="sm" className="max-w-[288px]">
              {CUSTOM_CLIENT_INPUT_TOOLTIP_TEXT}
            </Body>
          }
        >
          <Icon icon="Info" className="h-3 w-3 text-gray-small" />
        </Tooltip>
      </div>
      <Grid container spacing={2}>
        {clientCustomInputs.map(({ label, type, id, title, value }) => {
          const fieldValue = getFieldValue(value);

          return (
            <Grid item xs={12} key={id}>
              <div className={classes.flexContainer}>
                <SignatureSidebarButton
                  className={classes.customInputSignatureButton}
                  label={selectedRecipientId ? fieldValue : label}
                  onDrop={(coordinates) => {
                    handleComponentDropped(
                      coordinates,
                      SignatureComponentType.OWNER_TEXT,
                      fieldValue,
                      id,
                    );
                  }}
                  buttonIcon={
                    id === PredefinedAutofillKeys.Company
                      ? NoCompanyIcon
                      : MapFieldTypeToDefaultProps[type].icon
                  }
                  disabled={!selectedRecipientId}
                />

                <TruncatedText maxChars={12} text={title} />
              </div>
            </Grid>
          );
        })}
        <Grid item xs={12}>
          <div className={classes.flexContainer}>
            <SignatureSidebarButton
              label={moment().format(DEFAULT_DATE_FORMAT)}
              onDrop={(coordinates) => {
                handleComponentDropped(
                  coordinates,
                  SignatureComponentType.OWNER_DATE,
                  moment().format(DEFAULT_DATE_FORMAT),
                  PredefinedAutofillKeys.ShareDate,
                );
              }}
              buttonIcon={SignatureDateIcon}
              disabled={!selectedRecipientId}
            />
            <MemoBaseTypography>Share date</MemoBaseTypography>
          </div>
        </Grid>
      </Grid>
    </>
  );
};
